import { useState, useEffect, useRef, useCallback } from 'react';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, limit } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getUCDetailLookup, getUCList } from './ucLookupCache';

// Complaint status options
export const COMPLAINT_STATUS = {
  ACTION_REQUIRED: 'action_required',
  ACTION_TAKEN: 'action_taken',
  IRRELEVANT: 'irrelevant',
  RE_ASSIGN: 're_assign'
};

// Complaint approval options (only for irrelevent/re_assign)
export const COMPLAINT_APPROVAL = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'reject'
};

const useComplaints = (filters = {}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const prevFiltersRef = useRef(null);
  const isMountedRef = useRef(true);

  // Manual refetch function
  const refetch = useCallback(() => {
    prevFiltersRef.current = null; // Reset to force refetch
    setRefreshKey(prev => prev + 1);
  }, []);

  const district = filters?.district || '';
  const tahsil = filters?.tahsil || '';
  const uc = filters?.uc || '';
  const dogType = filters?.dogType || '';
  const startDate = filters?.startDate || '';
  const endDate = filters?.endDate || '';
  const searchQuery = filters?.searchQuery || '';
  const complaintStatus = filters?.complaintStatus || '';
  const complaintApproval = filters?.complaintApproval || '';
  const complainType = filters?.complainType || '';

  // Get UC lookup (uses shared cache)
  const getUCLookup = useCallback(() => getUCDetailLookup(), []);

  // Process and filter records
  const processRecords = useCallback((querySnapshot, ucLookup, startDate, endDate, searchQuery, statusFilter, approvalFilter, complainTypeFilter) => {
    let records = [];

    querySnapshot.forEach((docSnapshot) => {
      const docData = docSnapshot.data();

      // Handle createdAt - can be Firestore Timestamp or regular date
      let createdAtDate = null;
      if (docData.createdAt) {
        if (docData.createdAt.toDate) {
          createdAtDate = docData.createdAt.toDate();
        } else if (docData.createdAt.seconds) {
          createdAtDate = new Date(docData.createdAt.seconds * 1000);
        } else {
          createdAtDate = new Date(docData.createdAt);
        }
      }

      // Handle actionTakenAt timestamp
      let actionTakenAtDate = null;
      if (docData.actionTakenAt) {
        if (docData.actionTakenAt.toDate) {
          actionTakenAtDate = docData.actionTakenAt.toDate();
        } else if (docData.actionTakenAt.seconds) {
          actionTakenAtDate = new Date(docData.actionTakenAt.seconds * 1000);
        } else {
          actionTakenAtDate = new Date(docData.actionTakenAt);
        }
      }

      records.push({
        id: docSnapshot.id,
        // Old field mappings (for backward compatibility)
        name: docData.complainantName || docData.name || 'N/A',
        phone: docData.complainantPhone || docData.phone || 'N/A',
        cnic: docData.complainantCNIC || docData.cnic || docData.CNIC || docData.complainantCnic || 'N/A',
        address: docData.address || 'N/A',
        complaint: docData.description || docData.complaint || 'N/A',

        // New schema fields
        complaintId: docData.complaintId || null,
        complainantName: docData.complainantName || 'N/A',
        complainantPhone: docData.complainantPhone || 'N/A',
        complainantCNIC: docData.complainantCNIC || docData.cnic || docData.CNIC || docData.complainantCnic || 'N/A',
        complainantAddress: docData.complainantAddress || 'N/A',
        description: docData.description || 'N/A',
        complainType: docData.complainType || null,
        dogType: docData.dogType || 'N/A',

        // Location
        district: docData.district || 'Vehari',
        tahsil: docData.tahsil || 'N/A',
        ucId: docData.ucId || null,
        ucName: ucLookup[docData.ucId]?.uc || docData.ucId || null,

        // Coordinates
        complainCoordinates: docData.complainCoordinates || null,
        actionCoordinates: docData.actionCoordinates || null,
        latitude: docData.complainCoordinates?.lat || docData.coordinates?.lat || null,
        longitude: docData.complainCoordinates?.lng || docData.coordinates?.lng || null,

        // Status tracking
        complaintStatus: docData.complaintStatus || 'action_required',
        complaintApproval: docData.complaintApproval || null,
        requestType: docData.requestType || null,
        reason: docData.reason || null,
        actionTakenAt: actionTakenAtDate,

        // Images
        complaintImage: docData.complaintImage || null,
        actionImage: docData.actionImage || null,

        // Metadata
        createdAt: docData.createdAt,
        updatedAt: docData.updatedAt || null,
        createdBy: docData.createdBy || docData.submittedBy || null,
        submittedBy: docData.submittedBy || docData.createdBy || null,
        source: docData.source || 'app',
        date: createdAtDate
      });
    });

    // Apply status filter
    if (statusFilter) {
      records = records.filter(record => record.complaintStatus === statusFilter);
    }

    // Apply approval filter
    if (approvalFilter) {
      records = records.filter(record => record.complaintApproval === approvalFilter);
    }

    // Apply complainType filter
    if (complainTypeFilter) {
      records = records.filter(record => record.complainType === complainTypeFilter);
    }

    // Apply date filter
    if (startDate || endDate) {
      records = records.filter(record => {
        if (!record.date) return false;
        if (startDate && endDate) {
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          return record.date >= start && record.date <= end;
        } else if (startDate) {
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          return record.date >= start;
        } else if (endDate) {
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          return record.date <= end;
        }
        return true;
      });
    }

    // Apply search filter
    if (searchQuery) {
      const search = searchQuery.toLowerCase();
      records = records.filter(record =>
        record.name?.toLowerCase().includes(search) ||
        record.complainantName?.toLowerCase().includes(search) ||
        record.phone?.includes(search) ||
        record.complainantPhone?.includes(search) ||
        record.cnic?.includes(search) ||
        record.complainantCNIC?.includes(search) ||
        record.address?.toLowerCase().includes(search) ||
        record.complaint?.toLowerCase().includes(search) ||
        record.description?.toLowerCase().includes(search) ||
        record.complaintId?.toLowerCase().includes(search) ||
        record.ucId?.toLowerCase().includes(search)
      );
    }

    // Sort by timestamp (newest first)
    records.sort((a, b) => {
      if (!a.date) return 1;
      if (!b.date) return -1;
      return b.date - a.date;
    });

    return records;
  }, []);

  // Fetch user details by ID
  const fetchUserById = useCallback(async (userId) => {
    if (!userId) return null;

    try {
      // Try UnionCouncils collection first (using uid field)
      const ucQuery = query(
        collection(db, 'UnionCouncils'),
        where('uid', '==', userId)
      );
      const ucSnapshot = await getDocs(ucQuery);

      if (!ucSnapshot.empty) {
        const userData = ucSnapshot.docs[0].data();
        return {
          id: ucSnapshot.docs[0].id,
          ...userData
        };
      }

      // Try Public collection if not found in UnionCouncils
      const publicDocRef = doc(db, 'Public', userId);
      const publicDoc = await getDoc(publicDocRef);

      if (publicDoc.exists()) {
        return {
          id: publicDoc.id,
          ...publicDoc.data()
        };
      }

      return null;
    } catch (err) {
      console.error('Error fetching user:', err);
      return null;
    }
  }, []);

  // Update complaint approval status (for admin)
  // If rejecting irrelevant: complaintStatus becomes 'action_required', complaintApproval becomes null
  // If approving irrelevant: complaintApproval becomes 'approved'
  const updateComplaintApproval = useCallback(async (complaintId, approval) => {
    try {
      const complaintRef = doc(db, 'Complaints', complaintId);
      const updateData = {
        updatedAt: new Date()
      };

      if (approval === 'reject') {
        // Rejecting irrelevant claim - UC must take action
        updateData.complaintStatus = 'action_required';
        updateData.complaintApproval = null;
      } else {
        // Approving irrelevant claim
        updateData.complaintApproval = approval;
      }

      await updateDoc(complaintRef, updateData);
      refetch();
      return { success: true };
    } catch (err) {
      console.error('Error updating complaint approval:', err);
      return { success: false, error: err.message };
    }
  }, [refetch]);

  // Fetch all Union Councils for selection (uses shared cache)
  const fetchUnionCouncils = useCallback(async () => {
    try {
      return await getUCList();
    } catch (err) {
      console.error('Error fetching union councils:', err);
      return [];
    }
  }, []);

  // Re-assign complaint to different UC (when admin approves re-assign request)
  // ucId updated to new UC, complaintStatus becomes 'action_required', complaintApproval becomes null
  const reassignComplaint = useCallback(async (complaintId, newUcId) => {
    try {
      const complaintRef = doc(db, 'Complaints', complaintId);
      await updateDoc(complaintRef, {
        ucId: newUcId,
        complaintStatus: 'action_required',
        complaintApproval: null,
        updatedAt: new Date()
      });
      refetch();
      return { success: true };
    } catch (err) {
      console.error('Error reassigning complaint:', err);
      return { success: false, error: err.message };
    }
  }, [refetch]);

  // Reject re-assign request (keep with same UC)
  // ucId stays same, complaintStatus becomes 'action_required', complaintApproval becomes null
  const rejectReassign = useCallback(async (complaintId) => {
    try {
      const complaintRef = doc(db, 'Complaints', complaintId);
      await updateDoc(complaintRef, {
        complaintStatus: 'action_required',
        complaintApproval: null,
        updatedAt: new Date()
      });
      refetch();
      return { success: true };
    } catch (err) {
      console.error('Error rejecting reassign:', err);
      return { success: false, error: err.message };
    }
  }, [refetch]);

  // Approve requestType Irrelevent - change complaintStatus to 'irrelevent'
  const approveIrrelevantRequest = useCallback(async (complaintId) => {
    try {
      const complaintRef = doc(db, 'Complaints', complaintId);
      await updateDoc(complaintRef, {
        complaintStatus: 'irrelevant',
        complaintApproval: 'approved',
        requestType: null,
        updatedAt: new Date()
      });
      refetch();
      return { success: true };
    } catch (err) {
      console.error('Error approving irrelevant request:', err);
      return { success: false, error: err.message };
    }
  }, [refetch]);

  // Reject requestType Irrelevent - keep complaintStatus as 'action_required'
  const rejectIrrelevantRequest = useCallback(async (complaintId) => {
    try {
      const complaintRef = doc(db, 'Complaints', complaintId);
      await updateDoc(complaintRef, {
        complaintStatus: 'action_required',
        complaintApproval: 'reject',
        requestType: null,
        reason: null,
        updatedAt: new Date()
      });
      refetch();
      return { success: true };
    } catch (err) {
      console.error('Error rejecting irrelevant request:', err);
      return { success: false, error: err.message };
    }
  }, [refetch]);

  // Approve requestType Reassign/reassign - clear UC fields
  const approveReassignRequest = useCallback(async (complaintId) => {
    try {
      const complaintRef = doc(db, 'Complaints', complaintId);
      await updateDoc(complaintRef, {
        ucId: null,
        ucMcNumber: null,
        ucMcType: null,
        complaintApproval: 'approved',
        requestType: null,
        reason: null, // Clear reason after approval as per flow implication
        updatedAt: new Date()
      });
      refetch();
      return { success: true };
    } catch (err) {
      console.error('Error approving reassign request:', err);
      return { success: false, error: err.message };
    }
  }, [refetch]);

  // Reject requestType Reassign/reassign - clear request
  const rejectReassignRequest = useCallback(async (complaintId) => {
    try {
      const complaintRef = doc(db, 'Complaints', complaintId);
      await updateDoc(complaintRef, {
        complaintStatus: 'action_required',
        complaintApproval: 'reject',
        requestType: null,
        reason: null,
        updatedAt: new Date()
      });
      refetch();
      return { success: true };
    } catch (err) {
      console.error('Error rejecting reassign request:', err);
      return { success: false, error: err.message };
    }
  }, [refetch]);



  useEffect(() => {
    isMountedRef.current = true;
    const filterKey = JSON.stringify({ district, tahsil, uc, dogType, startDate, endDate, searchQuery, complaintStatus, complaintApproval, complainType });

    if (prevFiltersRef.current === filterKey) {
      return;
    }
    prevFiltersRef.current = filterKey;

    const fetchData = async () => {
      if (isMountedRef.current) {
        setLoading(true);
      }

      try {
        // Fetch UC lookup first
        const ucLookup = await getUCLookup();

        // Build query for Complaints collection
        let q = query(collection(db, 'Complaints'), limit(1000));
        const conditions = [];

        if (district) conditions.push(where('district', '==', district));
        if (tahsil) conditions.push(where('tahsil', '==', tahsil));
        if (uc) conditions.push(where('ucId', '==', uc));
        if (dogType) conditions.push(where('dogType', '==', dogType));
        if (complainType) conditions.push(where('complainType', '==', complainType));

        if (conditions.length > 0) {
          q = query(collection(db, 'Complaints'), ...conditions, limit(1000));
        }

        const querySnapshot = await getDocs(q);
        let records = processRecords(querySnapshot, ucLookup, startDate, endDate, searchQuery, complaintStatus, complaintApproval, complainType);

        // Apply UC filter in memory (supports both ucId uid match and ucName/ucMcNumber match)
        if (uc) {
          records = records.filter(record =>
            record.ucId === uc ||
            record.ucName === uc ||
            ucLookup[record.ucId]?.uc === ucLookup[uc]?.uc
          );
        }

        if (isMountedRef.current) {
          setData(records);
          setError(null);
        }
      } catch (err) {
        console.error('Error fetching complaints:', err);
        if (isMountedRef.current) {
          setError(err.message);
        }
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMountedRef.current = false;
    };
  }, [district, tahsil, uc, dogType, startDate, endDate, searchQuery, complaintStatus, complaintApproval, complainType, getUCLookup, processRecords, refreshKey]);

  return {
    data,
    loading,
    error,
    fetchUserById,
    refetch,
    updateComplaintApproval,
    reassignComplaint,
    rejectReassign,
    fetchUnionCouncils,
    approveIrrelevantRequest,
    rejectIrrelevantRequest,
    approveReassignRequest,
    rejectReassignRequest
  };
};

export default useComplaints;
